import { useAuth } from '../context/AuthContext';
import { NAV_ACCESS, SYSTEM_ROLES } from '../auth/permissions';

// Sentinel space_id used when a system user selects the admin-only mode.
export const ADMIN_SPACE_ID    = '__admin__';
// Sentinel space_id used when a GMS accountant selects the finance portal mode.
export const FINANCE_SPACE_ID  = '__finance__';

export function usePermissions() {
  const { user, activeSpace } = useAuth();

  const systemRole = user?.systemRole ?? null;
  const entityId   = user?.entityId   ?? null;
  const spaceRoles = activeSpace?.roles ?? [];

  const isGlobalAdmin   = systemRole === SYSTEM_ROLES.GLOBAL_ADMIN;
  const isEntityAdmin   = systemRole === SYSTEM_ROLES.ENTITY_ADMIN;
  const isGmsAccountant = systemRole === SYSTEM_ROLES.GMS_ACCOUNTANT;
  const isSystemUser    = isGlobalAdmin || isEntityAdmin || isGmsAccountant;
  const isSpaceAdmin    = spaceRoles.includes('space_admin');
  const isAdminMode     = activeSpace?.spaceId === ADMIN_SPACE_ID;
  const isFinanceMode   = activeSpace?.spaceId === FINANCE_SPACE_ID;

  const hasSpaceRole = (...roles) => roles.some((r) => spaceRoles.includes(r));

  // ── Page-level access ───────────────────────────────────────────────────────
  // Checks whether the current user can see/enter a nav route.
  const canAccessNav = (segment) => {
    const entry = NAV_ACCESS[segment];
    if (!entry) return false;
    const rule = entry.access;
    if (!rule) return false;

    if (isAdminMode) {
      return rule.systemRoles ? rule.systemRoles.includes(systemRole) : false;
    }

    // Finance Portal mode — GMS accountant sees Dashboard, Payments, Charities, Advertisers
    if (isFinanceMode) {
      return segment === '/' || segment === '/payments'
        || segment === '/charities' || segment === '/advertisers';
    }

    if (activeSpace) {
      if (rule.spaceRoles?.some((r) => spaceRoles.includes(r))) return true;
      // gms_accountant system role only grants Finance Portal pages via isFinanceMode, not real spaces
      if (!isGmsAccountant && rule.systemRoles?.includes(systemRole)) return true;
      return false;
    }

    // No active space — still allow system-role users access to their designated routes
    if (rule.systemRoles?.includes(systemRole)) return true;

    return false;
  };

  // ── Intra-page feature access ───────────────────────────────────────────────
  // Returns true if the current user can see/use a named feature on a page.
  // No feature entry in permissions.js = open to everyone who can access the page.
  const canFeature = (route, feature) => {
    const rule = NAV_ACCESS[route]?.features?.[feature];
    if (!rule) return true;
    if (isAdminMode) return rule.systemRoles?.includes(systemRole) ?? false;
    if (rule.spaceRoles?.some((r) => spaceRoles.includes(r))) return true;
    if (rule.systemRoles?.includes(systemRole)) return true;
    return false;
  };

  return {
    systemRole,
    entityId,
    spaceRoles,
    isGlobalAdmin,
    isEntityAdmin,
    isGmsAccountant,
    isSystemUser,
    isSpaceAdmin,
    isAdminMode,
    isFinanceMode,
    hasSpaceRole,
    canAccessNav,
    canFeature,
  };
}
